import { motion } from 'framer-motion';
import { ArrowLeft, Camera, MapPin, Save, Search, Target, Upload, Users, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import loggingService from '../../services/loggingService';
import { Button, Card, SearchBar } from '../ui';
import './SiteManagement.css';
import SiteMap from './SiteMap';
import {
  createSite,
  getAvailableSupervisors,
  resolveSiteLogoUrl,
  updateGeofence,
  uploadSiteLogo,
} from './siteApi';

const DEFAULT_CENTER = [20.5937, 78.9629];

const SiteForm = ({ site = null, onBack, onSuccess, mode = 'create', isSidebar = false, onEditGeofence = null, onRadiusChange = null }) => {
  const [formData, setFormData] = useState({
    site_name: '',
    address: '',
    geofence_type: 'Polygon',
    coordinates: [],
    radius: 100,
    center: [...DEFAULT_CENTER], // Default to India center
    authorised_ids: [],
    bluetooth_enabled: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [geofenceMode, setGeofenceMode] = useState('Polygon');
  const [geofenceHistory, setGeofenceHistory] = useState({
    past: [],
    future: [],
  });
  const [newSupervisor, setNewSupervisor] = useState('');
  const [availableSupervisors, setAvailableSupervisors] = useState([]);
  const [supervisorsLoading, setSupervisorsLoading] = useState(false);
  const [isGeofenceEditingEnabled, setIsGeofenceEditingEnabled] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [mapServices, setMapServices] = useState({
    map: null,
    google: null,
    geocoder: null,
    placesService: null,
    autocompleteService: null,
  });
  const [logoData, setLogoData] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const fileInputRef = useRef(null);

  const normalizeCenter = center => {
    if (!center) {
      return [...DEFAULT_CENTER];
    }

    if (Array.isArray(center) && center.length >= 2) {
      const lat = Number(center[0]);
      const lng = Number(center[1]);
      const latValue = Number.isFinite(lat) ? lat : DEFAULT_CENTER[0];
      const lngValue = Number.isFinite(lng) ? lng : DEFAULT_CENTER[1];
      return [latValue, lngValue];
    }

    if (typeof center === 'object') {
      const latRaw = center.lat ?? center.latitude;
      const lngRaw = center.lng ?? center.longitude;
      const latValue = Number(latRaw);
      const lngValue = Number(lngRaw);
      if (Number.isFinite(latValue) && Number.isFinite(lngValue)) {
        return [latValue, lngValue];
      }
    }

    return [...DEFAULT_CENTER];
  };

  const normalizeCoordinates = coords => {
    if (!Array.isArray(coords)) {
      return [];
    }

    return coords
      .map(coord => {
        if (Array.isArray(coord) && coord.length >= 2) {
          const lat = Number(coord[0]);
          const lng = Number(coord[1]);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return [lat, lng];
          }
          return null;
        }

        if (coord && typeof coord === 'object') {
          const latRaw = coord.lat ?? coord.latitude;
          const lngRaw = coord.lng ?? coord.longitude;
          const lat = Number(latRaw);
          const lng = Number(lngRaw);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return [lat, lng];
          }
        }

        return null;
      })
      .filter(Boolean);
  };

  const almostEqual = (a, b, tolerance = 1e-6) => {
    const numA = Number(a);
    const numB = Number(b);
    if (!Number.isFinite(numA) || !Number.isFinite(numB)) {
      return false;
    }
    return Math.abs(numA - numB) <= tolerance;
  };

  const areCentersEqual = (centerA, centerB) => {
    if (!Array.isArray(centerA) || !Array.isArray(centerB)) {
      return false;
    }
    return (
      almostEqual(centerA[0], centerB[0]) && almostEqual(centerA[1], centerB[1])
    );
  };

  const areCoordinateListsEqual = (coordsA, coordsB) => {
    if (!Array.isArray(coordsA) || !Array.isArray(coordsB)) {
      return false;
    }
    if (coordsA.length !== coordsB.length) {
      return false;
    }

    return coordsA.every((coord, index) => {
      const comparison = coordsB[index];
      if (!Array.isArray(coord) || !Array.isArray(comparison)) {
        return false;
      }
      return (
        almostEqual(coord[0], comparison[0]) &&
        almostEqual(coord[1], comparison[1])
      );
    });
  };

  const getCurrentGeofenceSnapshot = () => ({
    geofence_type: formData.geofence_type,
    coordinates: normalizeCoordinates(formData.coordinates),
    radius: Number(formData.radius) || 0,
    center: normalizeCenter(formData.center),
  });

  const geofenceStatesEqual = (stateA, stateB) => {
    if (!stateA || !stateB) {
      return false;
    }

    if (stateA.geofence_type !== stateB.geofence_type) {
      return false;
    }

    if (stateA.geofence_type === 'Circle') {
      return (
        areCentersEqual(stateA.center, stateB.center) &&
        almostEqual(stateA.radius, stateB.radius)
      );
    }

    if (stateA.geofence_type === 'Polygon') {
      return areCoordinateListsEqual(stateA.coordinates, stateB.coordinates);
    }

    return false;
  };

  const clearGeofenceErrors = () => {
    setErrors(prev => {
      if (!prev.coordinates && !prev.radius && !prev.center) {
        return prev;
      }
      const updated = { ...prev };
      delete updated.coordinates;
      delete updated.radius;
      delete updated.center;
      return updated;
    });
  };

  const applyGeofenceState = state => {
    if (!state) {
      return;
    }

    setFormData(prev => {
      const updated = { ...prev, geofence_type: state.geofence_type };

      if (state.geofence_type === 'Circle') {
        const centerToUse = state.center ?? prev.center;
        const radiusRaw =
          state.radius !== undefined ? Number(state.radius) : prev.radius;
        const radiusValue = Number.isFinite(radiusRaw) ? radiusRaw : prev.radius;

        updated.center = [...normalizeCenter(centerToUse)];
        updated.radius = radiusValue;
        updated.coordinates = [];
      } else if (state.geofence_type === 'Polygon') {
        const coordsToUse = state.coordinates ?? prev.coordinates;
        updated.coordinates = normalizeCoordinates(coordsToUse).map(coord => [
          ...coord,
        ]);

        if (state.center) {
          updated.center = [...normalizeCenter(state.center)];
        }

        if (state.radius !== undefined) {
          const radiusRaw = Number(state.radius);
          if (Number.isFinite(radiusRaw)) {
            updated.radius = radiusRaw;
          }
        }
      }

      return updated;
    });

    setGeofenceMode(state.geofence_type === 'Circle' ? 'Circle' : 'Polygon');
    clearGeofenceErrors();
  };

  const mapEventToGeofenceState = (geofenceData, currentSnapshot) => {
    if (!geofenceData || !geofenceData.type) {
      return null;
    }

    if (geofenceData.type === 'Circle') {
      const center = normalizeCenter(
        geofenceData.center ?? currentSnapshot.center
      );
      const radiusRaw =
        geofenceData.radius !== undefined
          ? Number(geofenceData.radius)
          : currentSnapshot.radius;
      const radiusValue = Number.isFinite(radiusRaw)
        ? radiusRaw
        : currentSnapshot.radius;

      return {
        geofence_type: 'Circle',
        center,
        radius: radiusValue,
        coordinates: [],
      };
    }

    if (geofenceData.type === 'Polygon') {
      return {
        geofence_type: 'Polygon',
        coordinates: normalizeCoordinates(geofenceData.coordinates),
        center: [...currentSnapshot.center],
        radius: currentSnapshot.radius,
      };
    }

    return null;
  };

  const handleUndoGeofence = () => {
    setGeofenceHistory(prevHistory => {
      if (!prevHistory.past.length) {
        return prevHistory;
      }

      const previousState =
        prevHistory.past[prevHistory.past.length - 1];
      const newPast = prevHistory.past.slice(0, -1);
      const currentSnapshot = getCurrentGeofenceSnapshot();

      applyGeofenceState(previousState);

      return {
        past: newPast,
        future: [currentSnapshot, ...prevHistory.future],
      };
    });
  };

  const handleRedoGeofence = () => {
    setGeofenceHistory(prevHistory => {
      if (!prevHistory.future.length) {
        return prevHistory;
      }

      const nextState = prevHistory.future[0];
      const newFuture = prevHistory.future.slice(1);
      const currentSnapshot = getCurrentGeofenceSnapshot();

      applyGeofenceState(nextState);

      return {
        past: [...prevHistory.past, currentSnapshot],
        future: newFuture,
      };
    });
  };

  useEffect(() => {
    if (site && mode === 'edit') {
      initializeFormData();
    }
  }, [site, mode, site?.geofence_details]);

  useEffect(() => {
    let cancelled = false;

    const loadExistingLogo = async () => {
      if (!site || mode !== 'edit') {
        return;
      }

      const existingLogoUrl = await resolveSiteLogoUrl(site);
      if (cancelled) {
        if (existingLogoUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(existingLogoUrl);
        }
        return;
      }

      setLogoData(null);
      setLogoPreview(existingLogoUrl);
      setHasLogo(false);
    };

    loadExistingLogo();

    return () => {
      cancelled = true;
    };
  }, [site, mode, site?.logo_key, site?.logo_presigned_url, site?.logo_url]);

  // Sync radius from site geofence_details when it changes (e.g., from map edits)
  useEffect(() => {
    if (site && mode === 'edit' && site.geofence_details) {
      try {
        const geofenceDetails = 
          typeof site.geofence_details === 'string'
            ? JSON.parse(site.geofence_details)
            : site.geofence_details;

        // Update radius if it's a Circle and the radius has changed
        if (geofenceDetails.type === 'Circle' && geofenceDetails.radius) {
          const newRadius = geofenceDetails.radius;
          // Update radius using functional update to avoid dependency on formData.radius
          setFormData(prev => {
            // Only update if the value is different to avoid unnecessary re-renders
            if (prev.radius !== newRadius) {
              return {
                ...prev,
                radius: newRadius,
              };
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('Error syncing radius from site:', error);
      }
    }
  }, [site?.geofence_details, mode]);

  // Reset geofence editing state only when switching to a different site in edit mode
  useEffect(() => {
    if (site && mode === 'edit') {
      setIsGeofenceEditingEnabled(false);
    }
  }, [site?.site_id, mode]);

  // Cleanup logo preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  // Fetch available supervisors when component mounts
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        setSupervisorsLoading(true);
        const supervisors = await getAvailableSupervisors();
        setAvailableSupervisors(supervisors);
        
        // Log API event for fetching supervisors
        try {
          await loggingService.logInfo(
            'site_form_fetch_supervisors',
            'Fetched available supervisors',
            {
              endpoint: '/user/getbyrole',
              method: 'GET',
              supervisorsCount: Array.isArray(supervisors) ? supervisors.length : 0,
            }
          );
        } catch (logError) {
          console.warn('[SiteForm] Failed to log event:', logError);
        }
      } catch (error) {
        console.error('Error fetching supervisors:', error);
        // Don't show error to user, just log it
      } finally {
        setSupervisorsLoading(false);
      }
    };

    fetchSupervisors();
  }, []);

  const initializeFormData = () => {
    try {
      const geofenceDetails = site.geofence_details
        ? typeof site.geofence_details === 'string'
          ? JSON.parse(site.geofence_details)
          : site.geofence_details
        : { type: 'Polygon', coordinates: [] };

      const normalizedCoordinates = normalizeCoordinates(
        geofenceDetails.coordinates || []
      );
      const normalizedCenter = normalizeCenter(
        geofenceDetails.center || DEFAULT_CENTER
      );
      const radiusRaw =
        geofenceDetails.radius !== undefined
          ? Number(geofenceDetails.radius)
          : 100;
      const normalizedRadius = Number.isFinite(radiusRaw) ? radiusRaw : 100;

      setFormData({
        site_name: site.site_name || '',
        address: site.address || '',
        geofence_type: geofenceDetails.type || 'Polygon',
        coordinates:
          (geofenceDetails.type || 'Polygon') === 'Polygon'
            ? normalizedCoordinates
            : [],
        radius: normalizedRadius,
        center: [...normalizedCenter],
        authorised_ids: site.authorised_ids || [],
        bluetooth_enabled:
          site.bluetooth_enabled !== undefined ? site.bluetooth_enabled : false,
      });

      setGeofenceMode(geofenceDetails.type === 'Circle' ? 'Circle' : 'Polygon');
      setGeofenceHistory({ past: [], future: [] });

      setLogoData(null);
      setLogoPreview(null);
      setHasLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error parsing site data:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size before processing (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setErrors(prev => ({
        ...prev,
        logo: `Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 5MB.`,
      }));
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        logo: 'Please select a valid image file.',
      }));
      return;
    }

    try {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.logo;
        return newErrors;
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setLogoData(base64);
        setLogoPreview(e.target.result);
        setHasLogo(true);
      };

      reader.onerror = () => {
        setErrors(prev => ({
          ...prev,
          logo: 'Failed to read image file. Please try again.',
        }));
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setErrors(prev => ({
        ...prev,
        logo: 'Failed to process image. Please try again.',
      }));
    }
  };

  const handleRemoveLogo = () => {
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoData(null);
    setLogoPreview(null);
    setHasLogo(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.logo;
      return newErrors;
    });
  };

  const handleGeofenceTypeChange = type => {
    if (!type || type === formData.geofence_type) {
      return;
    }

    const currentSnapshot = getCurrentGeofenceSnapshot();

    setGeofenceHistory(prev => ({
      past: [...prev.past, currentSnapshot],
      future: [],
    }));

    if (type === 'Circle') {
      applyGeofenceState({
        geofence_type: 'Circle',
        center: [...DEFAULT_CENTER],
        radius: 100,
        coordinates: [],
      });
    } else {
      applyGeofenceState({
        geofence_type: 'Polygon',
        coordinates: normalizeCoordinates(formData.coordinates),
        center: currentSnapshot.center,
        radius: currentSnapshot.radius,
      });
    }
  };

  // Handler for geofence changes from map
  const handleGeofenceChangeFromMap = geofenceData => {
    const currentSnapshot = getCurrentGeofenceSnapshot();
    const nextState = mapEventToGeofenceState(geofenceData, currentSnapshot);

    if (!nextState) {
      return;
    }

    if (geofenceStatesEqual(currentSnapshot, nextState)) {
      return;
    }

    setGeofenceHistory(prev => ({
      past: [...prev.past, currentSnapshot],
      future: [],
    }));

    applyGeofenceState(nextState);
  };

  const addSupervisor = () => {
    if (
      newSupervisor.trim() &&
      !formData.authorised_ids.includes(newSupervisor.trim())
    ) {
      setFormData(prev => ({
        ...prev,
        authorised_ids: [...prev.authorised_ids, newSupervisor.trim()],
      }));
      setNewSupervisor('');
    }
  };

  const addSupervisorById = supervisorId => {
    if (supervisorId && !formData.authorised_ids.includes(supervisorId)) {
      setFormData(prev => ({
        ...prev,
        authorised_ids: [...prev.authorised_ids, supervisorId],
      }));
    }
  };

  const fetchLocationSuggestions = value => {
    if (!value.trim()) {
      setSearchSuggestions([]);
      return;
    }

    const { autocompleteService } = mapServices;
    if (!autocompleteService || !window.google?.maps?.places) {
      return;
    }

    autocompleteService.getPlacePredictions(
      {
        input: value,
        types: ['geocode'],
      },
      (predictions, status) => {
        if (status === 'OK' && Array.isArray(predictions)) {
          const mappedSuggestions = predictions.map(prediction => ({
            label: prediction.description,
            placeId: prediction.place_id,
            mainText: prediction.structured_formatting?.main_text,
            secondaryText: prediction.structured_formatting?.secondary_text,
          }));
          setSearchSuggestions(mappedSuggestions);
        } else {
          setSearchSuggestions([]);
        }
      }
    );
  };

  const applySearchLocation = (lat, lng, label) => {
    const latitude = Number(lat);
    const longitude = Number(lng);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    if (label) {
      setSearchTerm(label);
    }
    setSearchSuggestions([]);

    handleInputChange('center', [latitude, longitude]);

    const { map } = mapServices;
    if (map) {
      map.panTo({ lat: latitude, lng: longitude });
      map.setZoom(15);
    }
  };

  const geocodeSearchLocation = query => {
    const trimmed = (query || '').trim();
    if (!trimmed) {
      return;
    }

    const { geocoder } = mapServices;
    if (!geocoder) {
      return;
    }

    geocoder.geocode({ address: trimmed }, (results, status) => {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) {
        return;
      }

      const location = results[0].geometry.location;
      const label = results[0].formatted_address || trimmed;
      applySearchLocation(location.lat(), location.lng(), label);
    });
  };

  const handleSuggestionSelect = suggestion => {
    if (!suggestion) {
      return;
    }

    const label = suggestion.label || suggestion.mainText || '';
    setSearchTerm(label);
    setSearchSuggestions([]);

    const { placesService } = mapServices;
    if (!suggestion.placeId || !placesService) {
      geocodeSearchLocation(label);
      return;
    }

    placesService.getDetails(
      {
        placeId: suggestion.placeId,
        fields: ['geometry', 'formatted_address', 'name'],
      },
      (place, status) => {
        if (status === 'OK' && place?.geometry?.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const displayLabel =
            place.formatted_address || place.name || label;
          applySearchLocation(lat, lng, displayLabel);
        } else {
          geocodeSearchLocation(label);
        }
      }
    );
  };

  const handleLocationSearch = value => {
    const trimmed = (value || searchTerm).trim();
    if (!trimmed) {
      return;
    }

    if (searchSuggestions.length > 0) {
      handleSuggestionSelect(searchSuggestions[0]);
      return;
    }

    geocodeSearchLocation(trimmed);
  };

  const removeSupervisor = index => {
    setFormData(prev => ({
      ...prev,
      authorised_ids: prev.authorised_ids.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.site_name.trim()) {
      newErrors.site_name = 'Site name is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (formData.geofence_type === 'Polygon') {
      if (!formData.coordinates || formData.coordinates.length < 3) {
        newErrors.coordinates =
          'Polygon must have at least 3 coordinate points';
      }
    } else if (formData.geofence_type === 'Circle') {
      if (!formData.radius || formData.radius <= 0) {
        newErrors.radius = 'Radius must be greater than 0';
      }
      if (!formData.center || formData.center.length !== 2) {
        newErrors.center = 'Center coordinates are required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const geofenceDetails = {
        type: formData.geofence_type,
        ...(geofenceMode === 'Polygon'
          ? {
              coordinates: formData.coordinates.map(coord => ({
                latitude: coord[0], // coord[0] is latitude (from Google Maps)
                longitude: coord[1], // coord[1] is longitude (from Google Maps)
              })),
            }
          : {
              radius: formData.radius,
              center: {
                latitude: formData.center[0], // center[0] is latitude (from Google Maps)
                longitude: formData.center[1], // center[1] is longitude (from Google Maps)
              },
            }),
      };

      if (mode === 'edit') {
        const updateData = {
          geofence_details: geofenceDetails,
          site_name: formData.site_name.trim(),
          address: formData.address.trim(),
          authorised_ids: formData.authorised_ids,
          bluetooth_enabled: formData.bluetooth_enabled,
        };

        await updateGeofence(site.site_id, updateData);

        if (hasLogo && logoData && logoPreview) {
          try {
            await uploadSiteLogo(site.site_id, logoPreview, logoData);
          } catch (logoError) {
            console.error('[SiteForm] Logo upload failed:', logoError);
            alert(
              'Site details were saved, but the logo could not be uploaded. Please try uploading the logo again.'
            );
          }
        }

        try {
          await loggingService.logInfo(
            'site_form_update_site',
            'Updated site',
            {
              endpoint: '/site/geofence/update',
              method: 'PUT',
              siteId: site.site_id,
              hasLogo: !!(hasLogo && logoData),
              supervisorsCount: formData.authorised_ids.length,
            }
          );
        } catch (logError) {
          console.warn('[SiteForm] Failed to log event:', logError);
        }
      } else {
        const siteData = {
          site_name: formData.site_name.trim(),
          address: formData.address.trim(),
          geofence_details: geofenceDetails,
          authorised_ids: formData.authorised_ids,
          bluetooth_enabled: formData.bluetooth_enabled,
        };

        const createResult = await createSite(siteData);
        const newSiteId =
          createResult?.site_id ?? createResult?.site?.site_id;

        if (hasLogo && logoData && logoPreview && newSiteId) {
          try {
            await uploadSiteLogo(newSiteId, logoPreview, logoData);
          } catch (logoError) {
            console.error('[SiteForm] Logo upload failed:', logoError);
            alert(
              'Site was created, but the logo could not be uploaded. Edit the site to try uploading the logo again.'
            );
          }
        }

        // Log API event for creating site
        try {
          await loggingService.logInfo(
            'site_form_create_site',
            'Created site',
            {
              endpoint: '/site/create',
              method: 'POST',
              siteName: siteData.site_name,
              geofenceType: geofenceDetails?.type || 'unknown',
              hasLogo: !!(hasLogo && logoData),
              supervisorsCount: siteData.authorised_ids.length,
            }
          );
        } catch (logError) {
          console.warn('[SiteForm] Failed to log event:', logError);
        }
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving site:', error);
      alert(`Failed to ${mode} site: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getGeofencePreview = () => {
    if (formData.geofence_type === 'Polygon') {
      return formData.coordinates.length > 0
        ? `${formData.coordinates.length} points`
        : 'No points added';
    } else {
      return formData.radius > 0
        ? `${formData.radius}m radius`
        : 'No radius set';
    }
  };

  // Check if valid coordinates are entered (not default India center or [0,0])
  const hasValidCoordinates = () => {
    if (!formData.center || formData.center.length !== 2) return false;
    const [lat, lng] = formData.center;
    // Check if coordinates are valid (not 0,0 and not default India center)
    const isDefaultIndia = lat === 20.5937 && lng === 78.9629;
    const isZero = lat === 0 && lng === 0;
    return !isDefaultIndia && !isZero && typeof lat === 'number' && typeof lng === 'number';
  };

  const isMapEditingEnabled = mode === 'create' || (mode === 'edit' && isGeofenceEditingEnabled);

  const renderFormContent = () => (
    <>
      {/* Row: Logo Upload (320px) + Basic Information (flex) */}
      <motion.div
        className="form-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Column 1: Logo Upload Section - Fixed 320px */}
          <div className="w-full lg:w-[320px] flex-shrink-0">
            <Card.Gradient className="p-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Site Logo
                </h3>

                <div className="flex flex-col items-center gap-4">
                  {/* Logo Preview */}
                  <div className="relative">
                    {logoPreview ? (
                      <div className="w-32 h-32 rounded-xl border-4 border-gray-300 overflow-hidden bg-white">
                        <img
                          src={logoPreview}
                          alt="Site Logo Preview"
                          className="w-full h-full object-contain"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                          style={{ fontSize: '20px', lineHeight: '1' }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-xl border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                        <Camera className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Upload Button */}
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                      style={{ color: '#ffffff' }}
                    >
                      <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                      Upload Logo
                    </Button>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  {errors.logo && (
                    <span className="error-message text-sm text-red-600">{errors.logo}</span>
                  )}
                </div>
              </div>
            </Card.Gradient>
          </div>

          {/* Column 2: Basic Information Section - Flex */}
          <div className="flex-1">
            <h3 className="section-title mb-4">Basic Information</h3>

            <div className="form-group">
              <label className="form-label">Site Name *</label>
              <input
                type="text"
                value={formData.site_name}
                onChange={e => handleInputChange('site_name', e.target.value)}
                className={`form-input ${errors.site_name ? 'error' : ''}`}
                placeholder="Enter site name"
              />
              {errors.site_name && (
                <span className="error-message">{errors.site_name}</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Address *</label>
              <textarea
                value={formData.address}
                onChange={e => handleInputChange('address', e.target.value)}
                className={`form-textarea ${errors.address ? 'error' : ''}`}
                placeholder="Enter full address"
                rows={3}
              />
              {errors.address && (
                <span className="error-message">{errors.address}</span>
              )}
            </div>
          </div>
        </div>
      </motion.div>

        {/* Map Integration - Only show when creating new site */}
        {mode !== 'edit' && (
          <motion.div
            className="form-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <h3 className="section-title">Map & Location</h3>

            {/* Search Bar Section */}
            <div className="mb-6 w-full flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <SearchBar
                  placeholder="Search location..."
                  value={searchTerm}
                  onChange={e => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    fetchLocationSuggestions(value);
                  }}
                  onSearch={handleLocationSearch}
                  suggestions={searchSuggestions}
                  onSuggestionClick={handleSuggestionSelect}
                  className="w-full"
                  iconType="search"
                  showSuggestions={searchTerm.trim().length > 0 && searchSuggestions.length > 0}
                  disabled={!mapServices.autocompleteService}
                />
              </div>
              <Button
                type="button"
                onClick={() => handleLocationSearch(searchTerm)}
                disabled={
                  !searchTerm.trim() ||
                  (!mapServices.geocoder && !mapServices.autocompleteService)
                }
                className="h-14 shrink-0 flex items-center justify-center gap-2 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-[10px] border border-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ color: '#ffffff' }}
                title="Search location on map"
              >
                <Search className="w-4 h-4" style={{ color: '#ffffff' }} />
                Search
              </Button>
            </div>

          

            <div className="map-container">
              <SiteMap
                center={formData.center}
                geofenceType={formData.geofence_type}
                coordinates={formData.coordinates}
                radius={formData.radius}
                isEditMode={isMapEditingEnabled}
                onCenterChange={(lat, lng) => {
                  const newCenter = [lat, lng];
                  if (formData.geofence_type === 'Circle') {
                    handleGeofenceChangeFromMap({
                      type: 'Circle',
                      center: newCenter,
                      radius: formData.radius,
                    });
                  } else {
                    handleInputChange('center', newCenter);
                  }
                }}
                onGeofenceChange={handleGeofenceChangeFromMap}
                onGeofenceTypeChange={handleGeofenceTypeChange}
                canUndo={geofenceHistory.past.length > 0}
                canRedo={geofenceHistory.future.length > 0}
                onUndo={handleUndoGeofence}
                onRedo={handleRedoGeofence}
                onMapReady={({
                  map,
                  google,
                  geocoder,
                  placesService,
                  autocompleteService,
                }) => {
                  setMapServices({
                    map: map || null,
                    google: google || null,
                    geocoder: geocoder || null,
                    placesService: placesService || null,
                    autocompleteService: autocompleteService || null,
                  });
                }}
              />
            </div>

            <div className="location-inputs">
              <div className="form-group">
                <label className="form-label">Latitude</label>
                <input
                  type="number"
                  value={formData.center[0] || ''}
                  onChange={e => {
                    const newCenter = [...formData.center];
                    newCenter[0] = parseFloat(e.target.value) || 0;
                    handleInputChange('center', newCenter);
                  }}
                  className="form-input"
                  step="any"
                  placeholder="e.g., 20.5937"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input
                  type="number"
                  value={formData.center[1] || ''}
                  onChange={e => {
                    const newCenter = [...formData.center];
                    newCenter[1] = parseFloat(e.target.value) || 0;
                    handleInputChange('center', newCenter);
                  }}
                  className="form-input"
                  step="any"
                  placeholder="e.g., 78.9629"
                />
              </div>
            </div>

            {/* Radius field below Latitude and Longitude */}
            {formData.geofence_type === 'Circle' && (
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Radius (in meters)</label>
                <input
                  type="number"
                  value={formData.radius}
                  onChange={e => {
                    const newRadius = parseFloat(e.target.value) || 0;
                    handleInputChange('radius', newRadius);
                  }}
                  className={`form-input ${errors.radius ? 'error' : ''} ${mode === 'create' && !hasValidCoordinates() ? 'disabled' : ''}`}
                  placeholder="Enter radius in meters"
                  min="1"
                  disabled={mode === 'create' && !hasValidCoordinates()}
                  title={mode === 'create' && !hasValidCoordinates() ? 'Please enter valid Latitude and Longitude first' : ''}
                />
                {mode === 'create' && !hasValidCoordinates() && (
                  <small style={{ display: 'block', marginTop: '4px', color: '#6c757d', fontStyle: 'italic' }}>
                    Enter valid Latitude and Longitude to enable radius input
                  </small>
                )}
                {errors.radius && (
                  <span className="error-message">{errors.radius}</span>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* Geofence Configuration */}
        <motion.div
          className="form-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <h3 className="section-title">Geofence Configuration</h3>

          <div className="geofence-type-selector">
            <label className="form-label">Geofence Type</label>
            <div className="geofence-options">
              
              <button
                type="button"
                className={`geofence-option ${formData.geofence_type === 'Circle' ? 'active' : ''} disabled`}
                disabled
                title="Geofence type selection is currently disabled."
              >
                <Target className="w-4 h-4" />
                <span>Circle (Simple)</span>
              </button>

              <button
                type="button"
                className={`geofence-option ${formData.geofence_type === 'Polygon' ? 'active' : ''} disabled`}
                disabled
                title="Geofence type selection is currently disabled."
              >
                <MapPin className="w-4 h-4" />
                <span>Polygon (Precise)</span>
              </button>
            </div>
          </div>

          <div className="geofence-preview">
            <span className="preview-label">Current Configuration:</span>
            <span className="preview-value">{getGeofencePreview()}</span>
          </div>

          {/* Edit Geofence Button - Only show in edit mode */}
          {mode === 'edit' && onEditGeofence && (
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => {
                  setIsGeofenceEditingEnabled(true);
                  onEditGeofence();
                }}
                className="edit-geofence-btn"
              >
                <Target className="w-4 h-4" />
                Edit Geofence
              </button>
            </div>
          )}

          {geofenceMode === 'Circle' && (
            <div className="form-group">
              <label className="form-label">Radius (in meters) *</label>
              {mode === 'create' && !hasValidCoordinates() && (
                <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px', fontStyle: 'italic' }}>
                  Please enter valid Latitude and Longitude to enable radius input
                </p>
              )}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <input
                  type="number"
                  value={formData.radius}
                  onChange={e => {
                    const newRadius = parseFloat(e.target.value) || 0;
                    handleInputChange('radius', newRadius);
                  }}
                  className={`form-input ${errors.radius ? 'error' : ''} ${(mode === 'edit' && !isGeofenceEditingEnabled) || (mode === 'create' && !hasValidCoordinates()) ? 'disabled' : ''}`}
                  style={{ flex: 1 }}
                  placeholder="Enter radius in meters"
                  min="1"
                  disabled={(mode === 'edit' && !isGeofenceEditingEnabled) || (mode === 'create' && !hasValidCoordinates())}
                  title={
                    mode === 'edit' && !isGeofenceEditingEnabled
                      ? 'Click "Edit Geofence" to enable radius editing'
                      : mode === 'create' && !hasValidCoordinates()
                      ? 'Please enter valid Latitude and Longitude first'
                      : ''
                  }
                />
                <button
                  type="button"
                  onClick={() => {
                    // Update the map on the left side when Set button is clicked
                    if (mode === 'edit' && isGeofenceEditingEnabled && onRadiusChange) {
                      onRadiusChange(formData.radius);
                    } else if (mode === 'create' && hasValidCoordinates()) {
                      // In create mode, update the geofence on the map by triggering the same handler
                      // that SiteMap uses, which will update the form data and display the circle
                      const geofenceData = {
                        type: 'Circle',
                        center: formData.center,
                        radius: formData.radius,
                      };
                      handleGeofenceChangeFromMap(geofenceData);
                    }
                  }}
                  className="set-radius-button"
                  disabled={(mode === 'edit' && !isGeofenceEditingEnabled) || (mode === 'create' && !hasValidCoordinates())}
                  style={{
                    padding: '16px 24px',
                    backgroundColor: (mode === 'edit' && !isGeofenceEditingEnabled) || (mode === 'create' && !hasValidCoordinates()) ? '#dee2e6' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: (mode === 'edit' && !isGeofenceEditingEnabled) || (mode === 'create' && !hasValidCoordinates()) ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => {
                    if (!((mode === 'edit' && !isGeofenceEditingEnabled) || (mode === 'create' && !hasValidCoordinates()))) {
                      e.target.style.backgroundColor = '#5a6fd8';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!((mode === 'edit' && !isGeofenceEditingEnabled) || (mode === 'create' && !hasValidCoordinates()))) {
                      e.target.style.backgroundColor = '#667eea';
                    }
                  }}
                >
                  Set
                </button>
              </div>
              {errors.radius && (
                <span className="error-message">{errors.radius}</span>
              )}
            </div>
          )}

          {errors.coordinates && (
            <span className="error-message">{errors.coordinates}</span>
          )}
        </motion.div>

        {/* Supervisors */}
        <motion.div
          className="form-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <h3 className="section-title">Authorized Supervisors</h3>

          <div className="supervisors-input">
            <div className="supervisor-input-group">
              {supervisorsLoading ? (
                <div className="loading-supervisors">
                  <span>Loading supervisors...</span>
                </div>
              ) : (
                <>
                  <select
                    value=""
                    onChange={e => {
                      if (e.target.value) {
                        addSupervisorById(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="form-input supervisor-select"
                  >
                    <option value="">Select a supervisor</option>
                    {availableSupervisors
                      .filter(
                        supervisor =>
                          !formData.authorised_ids.includes(supervisor.user_id)
                      )
                      .map(supervisor => (
                        <option
                          key={supervisor.user_id}
                          value={supervisor.user_id}
                        >
                          {supervisor.user_name || supervisor.user_id}
                        </option>
                      ))}
                  </select>
                  <div className="supervisor-input-info">
                    <small>
                      {availableSupervisors.length} supervisors available
                    </small>
                  </div>
                </>
              )}
            </div>
          </div>

          {formData.authorised_ids.length > 0 && (
            <div className="supervisors-list">
              {formData.authorised_ids.map((supervisorId, index) => {
                const supervisor = availableSupervisors.find(
                  s => s.user_id === supervisorId
                );
                return (
                  <div key={index} className="supervisor-tag">
                    <Users className="w-4 h-4" />
                    <span>
                      {supervisor
                        ? supervisor.user_name || supervisorId
                        : supervisorId}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSupervisor(index)}
                      className="remove-supervisor-button"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {availableSupervisors.length === 0 && !supervisorsLoading && (
            <div className="no-supervisors-available">
              <small>
                No supervisors available. Please check your permissions.
              </small>
            </div>
          )}
        </motion.div>

        {/* Settings */}
        <motion.div
          className="form-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <h3 className="section-title">Settings</h3>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.bluetooth_enabled}
                onChange={e =>
                  handleInputChange('bluetooth_enabled', e.target.checked)
                }
                className={`checkbox-input ${mode === 'edit' ? 'disabled' : ''}`}
                disabled={mode === 'edit'}
                title={mode === 'edit' ? 'Bluetooth setting cannot be changed in edit mode' : ''}
              />
              <span className={`checkbox-text ${mode === 'edit' ? 'disabled' : ''}`}>
                Enable Bluetooth for this site
              </span>
            </label>
          </div>
        </motion.div>

        {/* Submit Button */}
        <motion.div
          className="form-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <button
            type="button"
            onClick={onBack}
            className="cancel-button"
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? (
              <div className="loading-spinner small"></div>
            ) : (
              <Save className="w-4 h-4" />
            )}
            {mode === 'edit' ? 'Update Site' : 'Create Site'}
          </button>
        </motion.div>
    </>
  );

  return (
    <div className={`site-form-container ${isSidebar ? 'sidebar-mode' : ''}`}>
      {isSidebar ? (
        <>
          {/* Header */}
          <div className="site-form-header">
            <button onClick={onBack} className="back-button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="site-form-title">
              {mode === 'edit' ? 'Edit Site' : 'Create New Site'}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="site-form">
            {renderFormContent()}
          </form>
        </>
      ) : (
        <div className="site-form-wrapper">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8 pt-4 pb-2"
          >
            <h1
              className="text-3xl font-bold mb-2"
              style={{ color: '#000000' }}
            >
              {mode === 'edit' ? 'Edit Site' : 'Create New Site'}
            </h1>
            <p className="text-gray-600">
              {mode === 'edit' ? 'Update the site information below' : 'Complete the form below to create a new site'}
            </p>
          </motion.div>

          <form onSubmit={handleSubmit} className="h-full space-y-8">
            {renderFormContent()}
          </form>
        </div>
      )}
    </div>
  );
};

export default SiteForm;
